import React, { FC, Fragment } from 'react'
import { FormikProps, useField } from 'formik'

import { useAuthState } from '@pepper/ui'

import { getFieldComponent } from 'components/FormFields/FormFields'
import LanguageSelector from 'components/LanguageSelector/LanguageSelector'
import { useSelectedCompanyId } from 'utils/functions/misc'

import Search from './search'
import Switch from './switch/SwitchView'
import MultiCategoryToggle from './multiCategoryToggle/MultiCategoryToggle'
import DesignTemplate from './templateDesign/DesignTemplate'
import CheckboxRadio from './checkboxRadio/CheckboxRadio'
import WriterExpertise from './writerExpertise/WriterExpertise'
import ColorPicker from './colorPicker/ColorPicker'
import Button from './button/Button'
import Payment from './payment/Payments'
import PillsSelector from './pillsSelector/PillsSelector'
import URLList from './urlList/urlList'
import CustomSelectSearch from './customSelectSearch/customSelectSearch'
import { DragDropFileUploadField } from './draggerUploader/DraggerUploader'
import { useCompanyDetails } from 'utils/hooks/useCompanyDetails'
import styles from './_.module.css'
import DocsBriefComponent from './DocsBriefComponent/DocsBriefComponent'

type FormProps = Record<string, unknown>
type Props = {
  data: IProjectBody
  actions: FormikProps<any>
  draftData?: any
  paymentFormRef?: React.RefObject<any>
  updatePaymentCompletion?: (value: IPaymentStatus) => void
  companyCurrency?: string | null
  blockAllFormNavigation?: (val: boolean) => void
  blockBackFormNavigation?: (val: boolean) => void
  blockForwardFormNavigation?: (val: boolean) => void
  updatePosition?: (val: number) => void
  paymentExtraInfo?: any
  updateProjectFlow?: (
    values: FormProps,
    multiSelectValues?: IMultiSelectData | null,
    updateDraftOnDemand?: boolean,
    updatePaymentInfoAsSuccess?: boolean
  ) => void
  selectedBriefId?: string
  setSelectedBriefId?: (val: string) => void
  disableBriefSelection?: boolean
  showBriefModal?: boolean
  setShowBriefModal?: (val: boolean) => void
  onSearch?: (props: { key: string; value: string }) => void
}

const ComponentPicker: FC<Props> = ({
  data,
  actions,
  paymentFormRef,
  draftData,
  updatePaymentCompletion,
  companyCurrency,
  blockAllFormNavigation,
  blockBackFormNavigation,
  blockForwardFormNavigation,
  updatePosition,
  paymentExtraInfo,
  updateProjectFlow,
  selectedBriefId,
  setSelectedBriefId,
  disableBriefSelection,
  showBriefModal,
  setShowBriefModal,
  onSearch,
}) => {
  let primaryComponent
  let required = false
  const clearKey = data.clearKey || ''
  const { user } = useAuthState()
  const companyId = useSelectedCompanyId()
  const { data: company } = useCompanyDetails(companyId)

  if (
    data.validation?.find(validate => validate.type === 'required') ||
    data.conditionalValidation?.valid.find(
      validate => validate.type === 'required'
    )
  ) {
    required = true
  }
  let { disabled } = data
  let value
  const [individualField] = useField('individual')
  //  const [referencesField , _referencesMeta, referencesHelper] = useField('references')
  const [companyField, , companyHelper] = useField('company_name')
  if (data.conditionalValue === 'COMPANY_NAME_INDIVIDUAL') {
    const individualVal = individualField.value
    if (individualVal === 'Yes') {
      disabled = true
      value = `${user.first_name} ${user.last_name}`
      companyField.value !== value && companyHelper.setValue(value)
    }
  }
  let updateTooltipPosition
  if (data.tooltip) {
    updateTooltipPosition = updatePosition
  }
  if (data.type === 'refList') {
    return (
      <URLList
        name={data.id}
        miniTooltip={data.miniTooltip}
        label={data.label}
        required={required}
        actions={actions}
        defaultStaticValue={data.defaultStaticValue}
        disabled={disabled}
      />
    )
  }
  if (data.type === 'addOptionSelect') {
    return (
      <PillsSelector
        dataType={data.dataType}
        name={data.id}
        label={data.label}
        className=""
        required={required}
        placeHolder={data.placeHolder || ''}
        options={data.options}
        defaultStaticValue={data.defaultStaticValue}
        miniTooltip={data.miniTooltip}
        disabled={disabled}
      />
    )
  }
  if (data.type === 'input') {
    let typeData
    let activeClass = ''
    let inputType = 'text'
    switch (data.subType) {
      case 'singleRowNumber': {
        typeData = 'text'
        inputType = 'number'
        activeClass = styles.inputV2
        break
      }
      case 'singleRow':
      case 'singleRowText': {
        typeData = 'text'
        activeClass = styles.inputV2
        break
      }
      case 'multiRowText': {
        typeData = 'textarea'
        activeClass = styles.multiRowInput
        break
      }
      case 'richText': {
        typeData = 'editor'
        activeClass = styles.editorWrap
        break
      }
      default:
        typeData = 'text'
    }
    if (data.isAttachFile) {
      typeData = data.isVideoRecord ? 'editor' : 'comment-box'
    }

    let fieldProps: Record<string, unknown> = {
      placeholder: data.placeHolder,
      showGroup: true,
      tagField: data.tagField,
      tag: data.tag,
      disabled,
      inputType,
      loadV2: true,
      updatePosition: updateTooltipPosition,
      miniTooltip: data.miniTooltip,
      clearKey: data.clearKey,
      isVideoRecord: data.isVideoRecord,
      hideVideoUploadIcon: data.hideVideoUploadIcon,
      isAttachFile: data.isAttachFile,
      ...(data.minLength && { minLength: data.minLength }),
      ...(data.maxLength && { maxLength: data.maxLength }),
      ...(data.allowedFiles
        ? {
            allowedFiles: data.allowedFiles,
          }
        : {}),
      ...(data.fieldProps ?? {}),
    }
    if (value) {
      fieldProps = {
        ...fieldProps,
        value,
      }
    }
    primaryComponent = getFieldComponent({
      type: typeData,
      className: activeClass,
      name: data.id,
      label: data.label,
      fieldProps,
      required,
      actions,
    })
  } else if (
    data.type === 'select' ||
    // data.type === 'addOptionSelect' ||
    data.type === 'multi-select-tags'
  ) {
    primaryComponent = getFieldComponent({
      type: data.type,
      name: data.id,
      label: data.label,
      fieldProps: {
        body: data.body || [],
        placeholder: data.placeHolder,
        categoryId: paymentExtraInfo?.rootCategoryID || null,
        options: data.options,
        updatePosition: updateTooltipPosition,
        miniTooltip: data.miniTooltip,
        showSearch: data.showSearch,
        loadV2: true,
        shouldShowSearchV2: data.shouldShowSearchV2,
        defaultSearchHeader: data.defaultSearchHeader,
        searchPlaceHolder: data.searchPlaceHolder,
        disabled,
        shouldShowKeywordPicker: data.shouldShowKeywordPicker,
      },
      required,
      actions,
    })
  } else if (data.type === 'uploadFile') {
    primaryComponent = getFieldComponent({
      type: 'file',
      name: data.id,
      label: data.label,
      fieldProps: {
        multipleUpload: data.multipleUpload,
        miniTooltip: data.miniTooltip,
        updatePosition: updateTooltipPosition,
        multipleSelection: data.multipleSelection,
        loadV2: true,
        disabled,
      },
      className: styles.uploadV2,
      required,
      actions,
    })
  } else if (data.type === 'search') {
    primaryComponent = <Search searchOnChange={() => console.log('data')} />
  } else if (data.type === 'selectDimensions') {
    primaryComponent = getFieldComponent({
      type: 'selectDimensions',
      name: data.id,
      label: data.label,
      fieldProps: {
        options: data.options,
        loadV2: true,
        disabled,
      },
      required,
      actions,
    })
  } else if (data.type === 'multiBlockColorSelect') {
    primaryComponent = getFieldComponent({
      type: 'multiBlockSelect',
      name: data.id,
      label: data.label,
      fieldProps: {
        options: data.options,
        miniTooltip: data.miniTooltip,
        showPreview: false,
        loadV2: true,
        clearKey,
        disabled,
      },
      required,
      actions,
    })
  } else if (data.type === 'colorPicker') {
    primaryComponent = (
      <ColorPicker
        clearKey={clearKey}
        name={data.id}
        label={data.label}
        required={required}
        miniTooltip={data.miniTooltip}
        disabled={disabled}
      />
    )
  } else if (data.type === 'designTemplates') {
    primaryComponent = (
      <DesignTemplate
        label={data.label}
        required={required}
        name={data.id}
        subName={data.subId}
        disabled={disabled}
        {...{
          options: data.templateOptions,
        }}
      />
    )
  } else if (data.type === 'switch') {
    primaryComponent = (
      <Switch
        name={data.id}
        label={data.label}
        required={required}
        miniTooltip={data.miniTooltip}
        defaultStaticValue={data.defaultStaticValue}
        renderAsCheckbox={data.renderAsCheckbox}
        values={data.values}
        actions={actions}
        body={data.body}
        disabled={disabled}
      />
    )
  } else if (data.type === 'checkbox' || data.type === 'radio') {
    const customFieldProps = data.showCustomField
      ? {
          showCustomField: true as const,
          placeHolder: data.placeHolder || '',
          customFieldInfo: data.customFieldInfo || '',
          customFieldOptions: data.customFieldOptions,
        }
      : {}
    primaryComponent = (
      <CheckboxRadio
        singleSelect={data.type !== 'checkbox'}
        name={data.id}
        label={data.label}
        miniTooltip={data.miniTooltip}
        required={required}
        options={data.selectOptions}
        actions={actions}
        body={data.body}
        disabled={disabled}
        {...customFieldProps}
      />
    )
  } else if (data.type === 'writerProficiency') {
    primaryComponent = (
      <WriterExpertise
        name={data.id}
        label={data.label}
        miniTooltip={data.miniTooltip}
        required={required}
        values={data.writerOptions}
      />
    )
  } else if (data.type === 'centerAlignedButton') {
    primaryComponent = (
      <Button text={data.text} update={data.update} hideIcon={true} />
    )
  } else if (
    data.type === 'payment' &&
    draftData?.template_information?.assignments
  ) {
    primaryComponent = (
      <Payment
        companyCurrency={companyCurrency}
        updatePaymentCompletion={updatePaymentCompletion}
        data={draftData}
        paymentFormRef={paymentFormRef}
        assignmentsList={draftData.template_information.assignments}
        blockAllFormNavigation={blockAllFormNavigation}
        blockBackFormNavigation={blockBackFormNavigation}
        blockForwardFormNavigation={blockForwardFormNavigation}
        paymentExtraInfo={paymentExtraInfo}
        updateProjectFlow={updateProjectFlow}
      />
    )
  } else if (data.type === 'dragDrop') {
    primaryComponent = (
      <DragDropFileUploadField
        name={data.id}
        label={data.label}
        required={required}
        actions={actions}
        body={data.body}
        allowedFiles={data.allowedFiles}
        info={data.info}
        maxFileSize={data.maxFileSize}
        fieldProps={{
          multipleUpload: data.multipleUpload,
          multipleSelection: data.multipleSelection,
          disabled,
        }}
      />
    )
  } else if (data.type === 'multiCategoryToggle') {
    primaryComponent = (
      <MultiCategoryToggle
        name={data.id}
        verticalName={data.verticalName}
        verticalID={data.verticalID}
        rootVerticalName={data.rootVerticalName}
        label={data.label}
        required={required}
        miniTooltip={data.miniTooltip}
        defaultStaticValue={data.defaultStaticValue}
        renderAsCheckbox={data.renderAsCheckbox}
        actions={actions}
        body={data.body}
        onClick={data.onClick}
      />
    )
  } else if (data.type === 'customSelectSearch') {
    primaryComponent = (
      <div className={styles.customSelectsearchWrapper}>
        <CustomSelectSearch
          name={data.id}
          label={data.label}
          required={required}
          miniTooltip={data.miniTooltip}
          defaultStaticValue={data.defaultStaticValue}
          actions={actions}
          options={data.options as unknown as TemplateDetails[]}
          shouldShowViewTemplateButton={true}
        />
      </div>
    )
  } else if (data.type === 'customReportSearch') {
    const getReportsList = (reportData: ReportListObject[], type: string) => {
      return {
        key: type,
        list: reportData?.map(item => {
          return {
            header: item.topic,
            id: item.id,
          }
        }),
      }
    }

    primaryComponent = (
      <div className={styles.customSelectsearchWrapper}>
        <CustomSelectSearch<ReportListObject>
          name={data.id}
          label={data.label}
          required={required}
          miniTooltip={data.miniTooltip}
          defaultStaticValue={data.defaultStaticValue}
          actions={actions}
          options={data.options as unknown as ReportListObject[]}
          // TODO: will need to revert this false, since the page does not exist anymore
          shouldShowViewTemplateButton={false}
          imgHeaderText="No reports found"
          imgBottomText="Create a new report"
          onViewItemClick={id =>
            window.open(
              `${window.location.protocol}//${window.location.host}/${companyId}/content-research/${id}`,
              '_blank'
            )
          }
          viewItemButtonText="View Report"
          shouldShowAddItemButton={false}
          getItemsList={getReportsList}
          itemNameKey="topic"
          companyIdKey="company_id"
          onChange={searchVal =>
            onSearch?.({
              value: searchVal,
              key: 'topic',
            })
          }
        />
      </div>
    )
  } else if (data.type === 'languageSelect') {
    primaryComponent = (
      <LanguageSelector
        title={data.label}
        name={data.id}
        defaultValue={company?.settings?.language_preference}
      />
    )
  } else if (data.type === 'briefComponent' && setShowBriefModal) {
    primaryComponent = (
      <DocsBriefComponent
        setSelectedBriefId={setSelectedBriefId}
        selectedBriefId={selectedBriefId}
        actions={actions}
        disableBriefSelection={disableBriefSelection}
        showBriefModal={showBriefModal as boolean}
        setShowBriefModal={setShowBriefModal}
        projectCategoryId={draftData.project_category}
      />
    )
  } else {
    primaryComponent = getFieldComponent({
      type: data.type,
      name: data.id,
      label: data.label,
      fieldProps: {
        placeholder: data.placeHolder,
        loadV2: true,
        disabled,
      },
      required,
      actions,
    })
  }
  return <Fragment>{primaryComponent}</Fragment>
}

export default ComponentPicker
